import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { AdminShell, adminPage } from '#/components/admin/admin-shell'
import { DataTable } from '#/components/admin/ui'
import { VoucherForm } from '#/components/admin/voucher-form'
import { Badge } from '#/components/ui/badge'
import { Button } from '#/components/ui/button'
import { useAdminVoucherDelete, useAdminVouchers, type Voucher } from '#/lib/api-admin'
import { fmtDate } from '#/lib/format'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/admin/vouchers')({ component: adminPage(Page) })

function Page() {
  const { data, isLoading, isError, isFetching, refetch } = useAdminVouchers()
  const del = useAdminVoucherDelete()
  const [open, setOpen] = useState(false)
  const [sel, setSel] = useState<Voucher | null>(null)
  return (
    <AdminShell title={m.admin_nav_vouchers()} description={m.admin_vouchers_desc()} actions={<Button onClick={() => { setSel(null); setOpen(true) }}><Plus className="size-4" /> {m.admin_voucher_new()}</Button>}>
      <DataTable<Voucher>
        rows={data?.vouchers}
        loading={isLoading} error={isError} retrying={isFetching} onRetry={refetch}
        rowKey={(v) => v.id}
        onRow={(v) => { setSel(v); setOpen(true) }}
        cols={[
          { key: 'code', header: m.admin_col_code(), render: (v) => <span className="font-mono font-medium">{v.code}</span> },
          { key: 'kind', header: m.admin_col_value(), render: (v) => (v.kind === 'percent' ? `${v.value}%` : v.value.toLocaleString()) },
          { key: 'applies', header: m.admin_col_applies(), render: (v) => v.appliesTo?.join(', ') ?? <span className="text-muted-foreground">{m.admin_all_plans()}</span> },
          { key: 'auto', header: m.admin_col_auto(), render: (v) => (v.isAuto ? m.admin_yes() : '—') },
          { key: 'used', header: m.admin_col_used(), render: (v) => `${v.redeemedCount}${v.maxRedemption ? ` / ${v.maxRedemption}` : ''}`, className: 'text-right tabular-nums' },
          { key: 'dates', header: m.admin_col_period(), render: (v) => `${v.startsAt ? fmtDate(v.startsAt) : '…'} → ${v.endsAt ? fmtDate(v.endsAt) : '…'}` },
          { key: 'active', header: m.admin_col_active(), render: (v) => <Badge variant={v.isActive ? 'default' : 'secondary'}>{v.isActive ? m.admin_yes() : m.admin_no()}</Badge> },
          { key: 'del', header: '', render: (v) => <Button size="icon" variant="ghost" className="size-7" aria-label={m.common_delete()} onClick={(e) => { e.stopPropagation(); if (confirm(m.admin_voucher_delete_confirm())) del.mutate(v.id, { onSuccess: () => toast.success(m.admin_deleted()), onError: (er) => toast.error(er.message) }) }}><Trash2 className="size-4" /></Button> },
        ]}
      />
      <VoucherForm open={open} voucher={sel} onClose={() => setOpen(false)} />
    </AdminShell>
  )
}
